import { Grid3x3 } from 'lucide-react';
import React from 'react';

import type { SectorCorrelationMatrix } from '../types';
import { cn, correlationColor, sectorName } from '../utils/formatters';

interface Props {
    data: SectorCorrelationMatrix | null | undefined;
}

// Renders the SciPy Ward-seriated correlation matrix as an HTML grid. Cells are
// coloured by a diverging scale (rose = negative, indigo = positive) so the
// clustered blocks of co-moving sectors pop visually. Because the backend has
// already re-ordered rows/cols via hierarchical clustering, adjacent cells form
// the natural "defensive corner" / "cyclical corner" blocks.
export const SectorCorrelationHeatmap: React.FC<Props> = ({ data }) => {
    if (!data || !data.ordered_tickers?.length || !data.matrix?.length) {
        return (
            <div className="flex h-full min-h-[360px] items-center justify-center text-slate-400 text-sm font-medium border border-dashed border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900">
                No correlation data available.
            </div>
        );
    }

    const { ordered_tickers: labels, matrix, window_days } = data;
    const n = labels.length;
    // Reserve a wider first column for the ticker + sector-name row labels; the
    // matrix cells share the remaining space. Cells keep a sensible min width so
    // the stacked column headers (ticker over name) stay legible.
    const gridTemplate = `minmax(8.5rem, auto) repeat(${n}, minmax(3.25rem, 1fr))`;

    return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm p-5 md:p-6 flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center gap-3 mb-5 border-b border-slate-100 dark:border-slate-800 pb-4">
                <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg">
                    <Grid3x3 className="w-5 h-5" />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                        ETF Sector Correlation Matrix
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Discover {window_days}-day log-return correlations.
                    </p>
                </div>
            </div>

            {/* Grid */}
            <div className="overflow-x-auto flex-1">
                <div className="min-w-[760px]">
                    {/* Column header row */}
                    <div
                        className="grid gap-0.5 mb-0.5"
                        style={{ gridTemplateColumns: gridTemplate }}
                    >
                        <div />
                        {labels.map((t) => {
                            const name = sectorName(t);
                            return (
                                <div
                                    key={`col-${t}`}
                                    title={name ? `${t} · ${name}` : t}
                                    className="flex flex-col items-center justify-end py-1 px-0.5"
                                >
                                    <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 leading-tight">
                                        {t}
                                    </span>
                                    {name && (
                                        <span className="text-[8px] font-medium text-slate-400 dark:text-slate-500 leading-tight text-center line-clamp-2">
                                            {name}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Body rows */}
                    {matrix.map((row, i) => (
                        <div
                            key={`row-${labels[i]}`}
                            className="grid gap-0.5 mb-0.5"
                            style={{ gridTemplateColumns: gridTemplate }}
                        >
                            <div
                                title={
                                    sectorName(labels[i])
                                        ? `${labels[i]} · ${sectorName(labels[i])}`
                                        : labels[i]
                                }
                                className="flex items-baseline justify-end gap-1.5 pr-2 overflow-hidden"
                            >
                                {sectorName(labels[i]) && (
                                    <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 truncate">
                                        {sectorName(labels[i])}
                                    </span>
                                )}
                                <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 shrink-0">
                                    {labels[i]}
                                </span>
                            </div>
                            {row.map((rho, j) => {
                                const isDiag = i === j;
                                return (
                                    <div
                                        key={`cell-${i}-${j}`}
                                        title={`${labels[i]} · ${labels[j]}: ${rho.toFixed(2)}`}
                                        className={cn(
                                            'aspect-square flex items-center justify-center rounded-[3px] text-[9px] font-bold transition-transform hover:scale-110 hover:z-10 hover:ring-2 hover:ring-slate-400/40',
                                            isDiag
                                                ? 'text-slate-400 dark:text-slate-500'
                                                : Math.abs(rho) > 0.55
                                                  ? 'text-white'
                                                  : 'text-slate-700 dark:text-slate-200',
                                        )}
                                        style={{
                                            backgroundColor: isDiag
                                                ? 'rgba(148,163,184,0.25)'
                                                : correlationColor(rho),
                                        }}
                                    >
                                        {rho.toFixed(2)}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {/* Diverging legend */}
            <div className="mt-5 flex items-center justify-center gap-3 text-xs">
                <span className="font-medium text-rose-600 dark:text-rose-400">-1.0</span>
                <div
                    className="h-2.5 w-48 rounded-full"
                    style={{
                        background:
                            'linear-gradient(to right, rgba(244,63,94,0.9), rgba(148,163,184,0.25), rgba(79,70,229,0.9))',
                    }}
                />
                <span className="font-medium text-indigo-600 dark:text-indigo-400">+1.0</span>
            </div>
        </div>
    );
};

export default SectorCorrelationHeatmap;
